import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckOutlined, CloseOutlined, GiftOutlined } from '@ant-design/icons';
import { Modal, QRCode, Select, Spin, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { createPurchaseOrder, getOrderPaymentResult } from '../../../store/request';
import type {
  EntitlementOfferPrice,
  EntitlementOfferSpecification,
  EntitlementPackageOffer
} from '../../../types/catalog';
import type { PaymentCheckout } from '../../../types/purchase';
import { featureValue, formatMinor, currencySymbol, toCheckoutPayment, type PayChannel } from './pricingUtils';

const CODE_PATTERN = /^[0-9]{21}$/;
const POLL_INTERVAL_MS = 2500;

type EntitlementSkuOption = {
  key: string;
  skuCode: string;
  currency?: string;
  amountMinor?: number;
  currencyFractionDigits?: number;
  quotaLimit?: number;
  validityDays?: number | null;
};

type EntitlementPurchaseModalProps = {
  open: boolean;
  packages: EntitlementPackageOffer[];
  initialPackageCode?: string;
  initialSkuCode?: string;
  onClose: () => void;
  onPaid?: () => void;
};

function FeatureMark({ value }: { value: string }) {
  if (value === 'check') return <CheckOutlined className='pricing-feature-check' />;
  if (value === 'cross') return <CloseOutlined className='pricing-feature-cross' />;
  return <span className='pricing-feature-quota'>{value}</span>;
}

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 80);
  }
  return `pay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`.slice(0, 80);
}

function isHttpUrl(value: string) {
  return /^https?:\/\//i.test(value);
}

function isImageUrl(value: string) {
  return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(value);
}

function actionTypeOf(payment?: PaymentCheckout) {
  return payment?.actionType?.trim().toUpperCase() ?? '';
}

function packageCurrencies(offer?: EntitlementPackageOffer) {
  const codes = new Set<string>();
  (offer?.specifications ?? []).forEach((spec) => {
    (spec.prices ?? []).forEach((item) => {
      const code = item.currency?.trim().toUpperCase();
      if (code) codes.add(code);
    });
  });
  return Array.from(codes);
}

function flattenSkus(offer?: EntitlementPackageOffer): EntitlementSkuOption[] {
  const result: EntitlementSkuOption[] = [];
  (offer?.specifications ?? []).forEach((spec: EntitlementOfferSpecification, specIndex) => {
    (spec.prices ?? []).forEach((price: EntitlementOfferPrice, priceIndex) => {
      const skuCode = price.skuCode?.trim() ?? '';
      result.push({
        key: skuCode || `spec-${specIndex}-price-${priceIndex}`,
        skuCode,
        currency: price.currency,
        amountMinor: price.amountMinor,
        currencyFractionDigits: price.currencyFractionDigits,
        quotaLimit: spec.quotaLimit,
        validityDays: spec.validityDays
      });
    });
  });
  return result;
}

function moneyText(price?: Pick<EntitlementSkuOption, 'amountMinor' | 'currency' | 'currencyFractionDigits'>) {
  const amount = formatMinor(price?.amountMinor, price?.currencyFractionDigits);
  if (!amount) return '';
  const symbol = currencySymbol(price?.currency);
  return symbol ? `${symbol} ${amount}` : amount;
}

function validityText(
  days: number | null | undefined,
  t: (key: string, options?: { count: number }) => string
) {
  if (typeof days === 'number' && Number.isFinite(days)) return t('pricing.days', { count: days });
  return t('pricing.permanent');
}

function specTitle(
  item: EntitlementSkuOption,
  quotaUnit: string,
  t: (key: string, options?: { count: number }) => string
) {
  const limit =
    typeof item.quotaLimit === 'number' && Number.isFinite(item.quotaLimit) ? String(item.quotaLimit) : '';
  const unit = quotaUnit.trim();
  const quota = limit && unit ? `${limit}${unit}` : limit || '—';
  return `${quota} · ${validityText(item.validityDays, t)}`;
}

export function EntitlementPurchaseModal({
  open,
  packages,
  initialPackageCode,
  initialSkuCode,
  onClose,
  onPaid
}: EntitlementPurchaseModalProps) {
  const { t } = useTranslation();
  const [activePackageCode, setActivePackageCode] = useState('');
  const [skuCode, setSkuCode] = useState('');
  const [currency, setCurrency] = useState('');
  const [payChannel, setPayChannel] = useState<PayChannel>('wechat');
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState<{
    orderNo: string;
    payment?: PaymentCheckout;
  } | null>(null);
  const [polling, setPolling] = useState(false);
  const payAttempt = useRef({ payload: '', key: '' });
  const pollTimer = useRef<number | null>(null);
  const aliveRef = useRef(true);

  const activePackage = useMemo(() => {
    const code = activePackageCode.trim();
    if (code) {
      const matched = packages.find((item) => item.packageCode?.trim() === code);
      if (matched) return matched;
    }
    return packages[0];
  }, [activePackageCode, packages]);

  const currencies = useMemo(() => packageCurrencies(activePackage), [activePackage]);
  const quotaUnit = activePackage?.quotaUnit?.trim() ?? '';

  const skuOptions = useMemo(() => {
    const all = flattenSkus(activePackage);
    if (!currency) return all;
    const filtered = all.filter((item) => item.currency?.trim().toUpperCase() === currency);
    return filtered.length > 0 ? filtered : all;
  }, [activePackage, currency]);

  const selectedSku = useMemo(() => {
    const selected = skuCode.trim();
    if (selected) {
      const matched = skuOptions.find((item) => item.key === selected || item.skuCode === selected);
      if (matched) return matched;
    }
    return skuOptions[0];
  }, [skuCode, skuOptions]);

  const stopPolling = () => {
    if (pollTimer.current != null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
    setPolling(false);
  };

  const clearCheckout = () => {
    stopPolling();
    setCheckout(null);
    payAttempt.current = { payload: '', key: '' };
  };

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (pollTimer.current != null) {
        window.clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (pollTimer.current != null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
    setPolling(false);
    setCheckout(null);
    payAttempt.current = { payload: '', key: '' };
    setSubmitting(false);
    if (!open) return;
    const first = packages[0];
    const preferred =
      packages.find((item) => item.packageCode?.trim() === initialPackageCode?.trim()) ?? first;
    setActivePackageCode(preferred?.packageCode?.trim() || '');
    const codes = packageCurrencies(preferred);
    const nextCurrency = codes[0] ?? '';
    setCurrency(nextCurrency);
    const pool = flattenSkus(preferred).filter((item) =>
      nextCurrency ? item.currency?.trim().toUpperCase() === nextCurrency : true
    );
    const list = pool.length > 0 ? pool : flattenSkus(preferred);
    const preferredSku = initialSkuCode?.trim();
    const matched =
      (preferredSku && list.find((item) => item.skuCode === preferredSku)) || list[0];
    setSkuCode(matched?.key || matched?.skuCode || '');
    setPayChannel('wechat');
  }, [open, packages, initialPackageCode, initialSkuCode]);

  useEffect(() => {
    if (!open || !activePackage) return;
    const codes = packageCurrencies(activePackage);
    if (!codes.includes(currency)) {
      setCurrency(codes[0] ?? '');
    }
  }, [activePackage, currency, open]);

  useEffect(() => {
    if (!open) return;
    if (skuOptions.length === 0) {
      setSkuCode('');
      return;
    }
    const exists = skuOptions.some((item) => item.key === skuCode || item.skuCode === skuCode);
    if (!exists) {
      setSkuCode(skuOptions[0].key || skuOptions[0].skuCode);
    }
  }, [open, skuCode, skuOptions]);

  const amountText = moneyText(selectedSku);
  const canPay =
    !submitting &&
    Boolean(selectedSku?.skuCode?.trim()) &&
    Boolean(amountText) &&
    typeof selectedSku?.amountMinor === 'number';

  const selectPackage = (offer: EntitlementPackageOffer) => {
    clearCheckout();
    setActivePackageCode(offer.packageCode?.trim() || '');
  };

  const selectSku = (item: EntitlementSkuOption) => {
    clearCheckout();
    setSkuCode(item.key || item.skuCode);
  };

  const startPolling = (orderNo: string) => {
    stopPolling();
    setPolling(true);
    const tick = async () => {
      if (!aliveRef.current) return;
      try {
        const res = await getOrderPaymentResult(orderNo);
        if (!aliveRef.current) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('pricing.pay_create_failed'));
          pollTimer.current = window.setTimeout(tick, POLL_INTERVAL_MS);
          return;
        }
        const status = res.content?.status?.trim().toUpperCase() ?? '';
        if (status === 'SUCCEEDED') {
          stopPolling();
          setCheckout(null);
          message.success(res.message || t('pricing.pay_success'));
          payAttempt.current = { payload: '', key: '' };
          onPaid?.();
          onClose();
          return;
        }
        if (status === 'FAILED') {
          stopPolling();
          setCheckout(null);
          message.error(res.message || t('pricing.pay_failed'));
          return;
        }
        if (status === 'CLOSED') {
          stopPolling();
          setCheckout(null);
          message.warning(res.message || t('pricing.pay_closed'));
          return;
        }
      } catch {
        if (!aliveRef.current) return;
      }
      if (!aliveRef.current) return;
      pollTimer.current = window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    pollTimer.current = window.setTimeout(tick, POLL_INTERVAL_MS);
  };

  const applyPaymentAction = (payment?: PaymentCheckout) => {
    const actionValue = payment?.actionValue?.trim() ?? '';
    const actionType = actionTypeOf(payment);
    if (!actionValue) return;
    if (actionType.includes('REDIRECT') || actionType.includes('JUMP') || actionType.includes('LINK')) {
      if (isHttpUrl(actionValue) || /^(weixin|alipays):/i.test(actionValue)) {
        window.open(actionValue, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const pay = async () => {
    if (submitting) return;
    if (!canPay) {
      message.warning(t('pricing.unavailable'));
      return;
    }
    const packageCode = activePackage?.packageCode?.trim() ?? '';
    const skuId = selectedSku?.skuCode?.trim() ?? '';
    const currencyCode = selectedSku?.currency?.trim().toUpperCase() ?? currency.trim().toUpperCase();
    const amountMinor = selectedSku?.amountMinor;
    if (!CODE_PATTERN.test(skuId)) {
      message.warning(t('pricing.pay_invalid_sku'));
      return;
    }
    if (!CODE_PATTERN.test(packageCode)) {
      message.warning(t('pricing.pay_invalid_package'));
      return;
    }
    if (!/^[A-Z]{3}$/.test(currencyCode)) {
      message.warning(t('pricing.pay_invalid_amount'));
      return;
    }
    if (typeof amountMinor !== 'number' || !Number.isInteger(amountMinor) || amountMinor < 1) {
      message.warning(t('pricing.pay_invalid_amount'));
      return;
    }
    const gateway = toCheckoutPayment(payChannel);
    if (!gateway) {
      message.warning(t('pricing.pay_channel_unsupported'));
      return;
    }

    const payload = `${packageCode}|${skuId}|1|${currencyCode}|${amountMinor}|${gateway.channel}|${gateway.scene}`;
    if (payAttempt.current.payload !== payload || !payAttempt.current.key) {
      payAttempt.current = { payload, key: createIdempotencyKey() };
    }

    setSubmitting(true);
    try {
      const res = await createPurchaseOrder({
        skuId,
        quantity: 1,
        currency: currencyCode,
        idempotencyKey: payAttempt.current.key,
        payment: gateway,
        packageCode,
        amountMinor
      });
      if (!aliveRef.current) return;
      if (res.code !== 200 || !res.content?.order?.orderNo) {
        if (res.code !== -1) message.error(res.message || t('pricing.pay_create_failed'));
        return;
      }
      const orderNo = res.content.order.orderNo.trim();
      if (!CODE_PATTERN.test(orderNo)) {
        message.error(res.message || t('pricing.pay_create_failed'));
        return;
      }
      setCheckout({ orderNo, payment: res.content.payment });
      applyPaymentAction(res.content.payment);
      const paidAlready =
        res.content.payment?.status?.trim().toUpperCase() === 'SUCCEEDED' ||
        res.content.order.paymentStatus?.trim().toUpperCase() === 'PAID';
      if (paidAlready) {
        setCheckout(null);
        message.success(res.message || t('pricing.pay_success'));
        payAttempt.current = { payload: '', key: '' };
        onPaid?.();
        onClose();
        return;
      }
      startPolling(orderNo);
    } catch {
      if (aliveRef.current) message.error(t('pricing.pay_create_failed'));
    } finally {
      if (aliveRef.current) setSubmitting(false);
    }
  };

  const handleClose = () => {
    clearCheckout();
    onClose();
  };

  const handlePayQrClose = () => {
    clearCheckout();
  };

  const actionValue = checkout?.payment?.actionValue?.trim() ?? '';
  const showImageQr = Boolean(actionValue && isHttpUrl(actionValue) && isImageUrl(actionValue));
  const showEncodedQr = Boolean(actionValue && !showImageQr);
  const showOpenLink = Boolean(actionValue && (isHttpUrl(actionValue) || /^(weixin|alipays):/i.test(actionValue)));
  const payQrOpen = Boolean(open && checkout);

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        width={920}
        destroyOnClose
        centered
        className='pricing-purchase-modal'
        closeIcon={<CloseOutlined />}
      >
        <div className='pricing-purchase'>
          <div className='pricing-purchase-tabs' role='tablist'>
            {packages.map((offer, index) => {
              const code = offer.packageCode?.trim() || `package-${index}`;
              const active =
                (activePackage?.packageCode?.trim() || '') === (offer.packageCode?.trim() || '') ||
                (!activePackage?.packageCode && offer === activePackage);
              const tone = index % 2 === 0 ? 'pro' : 'deluxe';
              return (
                <button
                  key={code}
                  type='button'
                  role='tab'
                  aria-selected={active}
                  className={`pricing-purchase-tab${active ? ' is-active' : ''} pricing-purchase-tab--${tone}`}
                  onClick={() => selectPackage(offer)}
                >
                  <span className={`pricing-card-icon pricing-card-icon--${tone}`} aria-hidden>
                    <GiftOutlined />
                  </span>
                  <span>{offer.displayName?.trim() || '—'}</span>
                </button>
              );
            })}
          </div>

          <div className='pricing-purchase-body'>
            <div className='pricing-purchase-main'>
              <div className='pricing-purchase-skus'>
                {skuOptions.map((item) => {
                  const selected =
                    (selectedSku?.key || selectedSku?.skuCode) === item.key ||
                    selectedSku?.skuCode === item.skuCode;
                  const parts = moneyText(item);
                  return (
                    <button
                      key={item.key}
                      type='button'
                      className={`pricing-purchase-sku${selected ? ' is-selected' : ''}`}
                      onClick={() => selectSku(item)}
                    >
                      <div className='pricing-purchase-sku-title'>{specTitle(item, quotaUnit, t)}</div>
                      <div className='pricing-purchase-sku-price'>
                        {parts ? (
                          <span className='pricing-purchase-sku-amount'>{parts}</span>
                        ) : (
                          '—'
                        )}
                      </div>
                      {selected ? (
                        <span className='pricing-purchase-sku-check' aria-hidden>
                          <CheckOutlined />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <div className='pricing-purchase-field'>
                <div className='pricing-purchase-label'>{t('pricing.currency')}</div>
                <Select
                  className='pricing-purchase-select'
                  value={currency || undefined}
                  placeholder={t('pricing.currency')}
                  options={currencies.map((code) => ({
                    value: code,
                    label: code === 'CNY' ? t('pricing.currency_cny') : code
                  }))}
                  onChange={(value) => {
                    clearCheckout();
                    setCurrency(value);
                  }}
                />
              </div>

              <div className='pricing-purchase-field'>
                <div className='pricing-purchase-label'>{t('pricing.pay_method')}</div>
                <div className='pricing-purchase-pay-methods'>
                  {(
                    [
                      { key: 'wechat', label: t('pricing.pay_wechat'), icon: '/payway-wx.png' },
                      { key: 'alipay', label: t('pricing.pay_alipay'), icon: '/payway-alipay.png' }
                    ] as const
                  ).map((item) => (
                    <button
                      key={item.key}
                      type='button'
                      className={`pricing-purchase-pay-btn${payChannel === item.key ? ' is-selected' : ''}`}
                      onClick={() => {
                        clearCheckout();
                        setPayChannel(item.key);
                      }}
                    >
                      <img className='pricing-purchase-pay-icon' src={item.icon} alt='' aria-hidden />
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className='pricing-purchase-summary pt-4 pb-3 my-6'>
                <div className='pricing-purchase-summary-row'>
                  <span>
                    <i className='pricing-purchase-dot pricing-purchase-dot--plan' aria-hidden />
                    {t('pricing.plan_price')}
                  </span>
                  <strong>{amountText || '—'}</strong>
                </div>
                <div className='pricing-purchase-summary-row is-total'>
                  <span>
                    <i className='pricing-purchase-dot pricing-purchase-dot--pay' aria-hidden />
                    {t('pricing.pay_amount')}
                  </span>
                  <strong className='pricing-purchase-total'>{amountText || '—'}</strong>
                </div>
              </div>

              <div className='pricing-purchase-actions'>
                <button type='button' className='pricing-purchase-pay' disabled={!canPay} onClick={pay}>
                  {t('pricing.pay_now')}
                </button>
                <p className='pricing-purchase-agree'>{t('pricing.agree_terms')}</p>
              </div>
            </div>

            <aside className='pricing-purchase-side'>
              <h3 className='pricing-purchase-side-title'>{t('pricing.benefits')}</h3>
              <ul className='pricing-purchase-features'>
                {(activePackage?.featureDescriptions ?? []).map((feature, featureIndex) => {
                  const title = feature.title?.trim() || '—';
                  const key = `${feature.title ?? 'feature'}-${featureIndex}`;
                  if (feature.displayType === 'TEXT') {
                    return (
                      <li key={key} className='pricing-feature is-text'>
                        <span className='entitlement-card-feature-label'>{title}</span>
                        <p className='entitlement-card-feature-text'>{feature.text?.trim() || '—'}</p>
                      </li>
                    );
                  }
                  const value = featureValue(feature, t);
                  return (
                    <li key={key} className='pricing-feature'>
                      <span className='entitlement-card-feature-label'>{title}</span>
                      {value ? <FeatureMark value={value} /> : null}
                    </li>
                  );
                })}
              </ul>
            </aside>
          </div>
        </div>
      </Modal>

      <Modal
        open={payQrOpen}
        onCancel={handlePayQrClose}
        footer={null}
        width={400}
        centered
        destroyOnClose
        maskClosable={false}
        zIndex={1100}
        className='pricing-pay-qr-modal'
        closeIcon={<CloseOutlined />}
        title={t('pricing.pay_waiting')}
      >
        <div className='pricing-pay-qr'>
          {amountText ? <div className='pricing-pay-qr-amount'>{amountText}</div> : null}
          {checkout ? (
            <p className='pricing-pay-qr-order'>{t('pricing.pay_order_no', { orderNo: checkout.orderNo })}</p>
          ) : null}
          {showImageQr ? (
            <img className='pricing-pay-qr-image' src={actionValue} alt={t('pricing.pay_waiting')} />
          ) : null}
          {showEncodedQr ? (
            <div className='pricing-pay-qr-code'>
              <QRCode value={actionValue} size={200} />
            </div>
          ) : null}
          {actionValue ? <p className='pricing-pay-qr-hint'>{t('pricing.pay_qr_hint')}</p> : null}
          {showOpenLink ? (
            <button
              type='button'
              className='pricing-pay-qr-link'
              onClick={() => window.open(actionValue, '_blank', 'noopener,noreferrer')}
            >
              {t('pricing.pay_open_link')}
            </button>
          ) : null}
          {polling ? (
            <div className='pricing-pay-qr-poll'>
              <Spin size='small' />
              <span>{t('pricing.pay_polling')}</span>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
